import { useState, useEffect, useCallback } from 'react';
import { ExternalLink, AlertCircle, X, RefreshCw, Briefcase, Clock, Users, TrendingUp, Download } from 'lucide-react';
import api from '../../api/api';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

/**
 * EmployerReportingTab
 *
 * Props:
 *   employerId  null    → employer view: fetch from /jobs/employers/report (session-derived)
 *               number  → admin view: fetch from /jobs/employers/<id>/report
 *   isDarkMode  bool
 */
export default function EmployerReportingTab({ employerId, isDarkMode }) {
    const [data, setData]       = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError]     = useState(null);

    const card    = `rounded-xl border p-5 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`;
    const label   = `text-xs font-medium uppercase tracking-wide ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`;
    const bigVal  = `text-3xl font-bold mt-1 ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`;
    const subtext = `text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`;
    const th      = `px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide ${isDarkMode ? 'bg-slate-700/60 text-slate-400' : 'bg-slate-50 text-slate-500'}`;
    const td      = `px-4 py-2.5 text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`;
    const divider = `divide-y ${isDarkMode ? 'divide-slate-700' : 'divide-slate-100'}`;

    const fetchReport = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const url = typeof employerId === 'number'
                ? `/jobs/employers/${employerId}/report`
                : '/jobs/employers/report';
            const res = await api.get(url);
            setData(res.data);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to load report. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [employerId]);

    useEffect(() => {
        fetchReport();
    }, [fetchReport]);

    const handleDownload = useCallback((format) => {
        if (!data) return;
        const companyName = data.employer?.name || 'Employer';
        const fileName = companyName.replace(/\s+/g, '-').toLowerCase();

        if (format === 'excel') {
            const wb = XLSX.utils.book_new();

            // Summary sheet
            const summaryRows = [
                ['Company', companyName],
                ['Industry', data.employer?.industry || ''],
                ['Location', data.employer?.location || ''],
                ['Active Job Count', data.summary?.job_count ?? ''],
                ['Roles Synced', data.summary?.roles_synced ?? ''],
                ['Last Synced', data.summary?.last_synced ? new Date(data.summary.last_synced).toLocaleString() : 'Never'],
                ['Users Tracked', data.demographics?.total_trackers ?? ''],
            ];
            XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(summaryRows), 'Summary');

            // Jobs sheet
            const jobRows = (data.jobs || []).map(j => ({ Title: j.title || '', 'Date Posted': j.posted_on || '', URL: j.url || '' }));
            XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(jobRows), 'Job Listings');

            // Top roles sheet
            const roleRows = (data.demographics?.top_roles || []).map(r => ({ Role: r.role, Count: r.count }));
            XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(roleRows), 'Top Roles');

            XLSX.writeFile(wb, `${fileName}-report.xlsx`);
        } else {
            const doc = new jsPDF();
            let y = 16;

            doc.setFontSize(14);
            doc.text(`Employer Report — ${companyName}`, 14, y);
            y += 6;
            doc.setFontSize(9);
            doc.setTextColor(120);
            doc.text([data.employer?.industry, data.employer?.location].filter(Boolean).join(' · '), 14, y);
            doc.setTextColor(0);
            y += 8;

            // Summary table
            autoTable(doc, {
                startY: y,
                head: [['Metric', 'Value']],
                body: [
                    ['Active Job Count', String(data.summary?.job_count ?? '')],
                    ['Roles Synced', String(data.summary?.roles_synced ?? '')],
                    ['Last Synced', data.summary?.last_synced ? new Date(data.summary.last_synced).toLocaleString() : 'Never'],
                    ['Users Tracked', String(data.demographics?.total_trackers ?? '')],
                ],
                styles: { fontSize: 8 },
                headStyles: { fillColor: [16, 185, 129] },
            });
            y = doc.lastAutoTable.finalY + 8;

            // Jobs table
            doc.setFontSize(10);
            doc.text('Job Listings', 14, y);
            y += 4;
            autoTable(doc, {
                startY: y,
                head: [['Title', 'Date Posted']],
                body: (data.jobs || []).map(j => [j.title || '', j.posted_on || '']),
                styles: { fontSize: 8 },
                headStyles: { fillColor: [16, 185, 129] },
            });
            y = doc.lastAutoTable.finalY + 8;

            // Top roles table
            if (data.demographics?.top_roles?.length) {
                doc.setFontSize(10);
                doc.text('Top Job Roles Tracked', 14, y);
                y += 4;
                autoTable(doc, {
                    startY: y,
                    head: [['Role', 'Count']],
                    body: data.demographics.top_roles.map(r => [r.role, r.count]),
                    styles: { fontSize: 8 },
                    headStyles: { fillColor: [16, 185, 129] },
                });
            }

            doc.save(`${fileName}-report.pdf`);
        }
    }, [data]);

    if (loading) {
        return (
            <div className="flex items-center justify-center py-20">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-4 border-primary-500 mx-auto mb-3" />
                    <p className={subtext}>Loading report…</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Error banner */}
            {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                    <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={18} />
                    <p className="text-sm text-red-700 flex-1">{error}</p>
                    <button onClick={() => setError(null)} className="p-1 hover:bg-red-100 rounded">
                        <X size={14} className="text-red-500" />
                    </button>
                </div>
            )}

            {data && (
                <>
                    {/* Header row */}
                    <div className="flex items-center justify-between">
                        <div>
                            <h2 className={`text-xl font-bold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>
                                {data.employer.name}
                            </h2>
                            <p className={subtext}>
                                {[data.employer.industry, data.employer.location].filter(Boolean).join(' · ')}
                            </p>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={fetchReport}
                                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors
                                    ${isDarkMode ? 'border-slate-600 text-slate-300 hover:bg-slate-700' : 'border-slate-300 text-slate-600 hover:bg-slate-50'}`}
                            >
                                <RefreshCw size={13} /> Refresh
                            </button>
                            <button
                                onClick={() => handleDownload('excel')}
                                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors
                                    ${isDarkMode ? 'border-slate-600 text-slate-300 hover:bg-slate-700' : 'border-slate-300 text-slate-600 hover:bg-slate-50'}`}
                            >
                                <Download size={13} /> Excel
                            </button>
                            <button
                                onClick={() => handleDownload('pdf')}
                                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors
                                    ${isDarkMode ? 'border-slate-600 text-slate-300 hover:bg-slate-700' : 'border-slate-300 text-slate-600 hover:bg-slate-50'}`}
                            >
                                <Download size={13} /> PDF
                            </button>
                        </div>
                    </div>

                    {/* ── Summary Cards ── */}
                    <div className="grid grid-cols-3 gap-4">
                        <div className={card}>
                            <div className="flex items-center gap-2 mb-2">
                                <Briefcase size={16} className="text-primary-500" />
                                <span className={label}>Job Count</span>
                            </div>
                            <div className={bigVal}>{data.summary.job_count.toLocaleString()}</div>
                            <p className={`mt-1 ${subtext}`}>Active job listings</p>
                        </div>

                        <div className={card}>
                            <div className="flex items-center gap-2 mb-2">
                                <Clock size={16} className="text-blue-500" />
                                <span className={label}>Last Synced</span>
                            </div>
                            <div className={`text-lg font-semibold mt-1 ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>
                                {data.summary.last_synced
                                    ? new Date(data.summary.last_synced).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                                    : 'Never'}
                            </div>
                            {data.summary.last_synced && (
                                <p className={`mt-1 ${subtext}`}>
                                    {new Date(data.summary.last_synced).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                                </p>
                            )}
                        </div>

                        <div className={card}>
                            <div className="flex items-center gap-2 mb-2">
                                <TrendingUp size={16} className="text-violet-500" />
                                <span className={label}>Roles Synced</span>
                            </div>
                            <div className={bigVal}>{data.summary.roles_synced.toLocaleString()}</div>
                            <p className={`mt-1 ${subtext}`}>Positions in system</p>
                        </div>
                    </div>

                    {/* ── Job Posting Report ── */}
                    <div className={`rounded-2xl border shadow-sm overflow-hidden ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
                        <div className={`px-5 py-4 border-b ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                            <h3 className={`font-semibold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>
                                Job Posting Report
                            </h3>
                            <p className={`text-xs mt-0.5 ${subtext}`}>
                                {data.jobs.length} listing{data.jobs.length !== 1 ? 's' : ''} · sorted by date
                            </p>
                        </div>

                        {data.jobs.length === 0 ? (
                            <div className="py-12 text-center">
                                <p className={subtext}>No jobs found for this employer.</p>
                            </div>
                        ) : (
                            <div className="max-h-80 overflow-y-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr>
                                            <th className={th}>Title</th>
                                            <th className={th}>Date Posted</th>
                                            <th className={`${th} w-16 text-center`}>Link</th>
                                        </tr>
                                    </thead>
                                    <tbody className={divider}>
                                        {data.jobs.map((job, idx) => (
                                            <tr key={idx} className={isDarkMode ? 'hover:bg-slate-700/40' : 'hover:bg-slate-50'}>
                                                <td className={`${td} font-medium`}>{job.title || '—'}</td>
                                                <td className={`${td} whitespace-nowrap`}>{job.posted_on || '—'}</td>
                                                <td className="px-4 py-2.5 text-center">
                                                    {job.url ? (
                                                        <a
                                                            href={job.url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="inline-flex items-center justify-center text-primary-500 hover:text-primary-600 transition-colors"
                                                        >
                                                            <ExternalLink size={15} />
                                                        </a>
                                                    ) : (
                                                        <span className={`text-xs ${isDarkMode ? 'text-slate-600' : 'text-slate-300'}`}>—</span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    {/* ── User Demographics ── */}
                    <div className="grid grid-cols-2 gap-4">
                        {/* Users tracked card */}
                        <div className={card}>
                            <div className="flex items-center gap-2 mb-2">
                                <Users size={16} className="text-amber-500" />
                                <span className={label}>Users Tracked</span>
                            </div>
                            <div className={bigVal}>{data.demographics.total_trackers.toLocaleString()}</div>
                            <p className={`mt-1 ${subtext}`}>Unique users who have active tracked job(s) from this employer</p>
                        </div>

                        {/* Top roles card */}
                        <div className={card}>
                            <div className="flex items-center gap-2 mb-3">
                                <TrendingUp size={16} className="text-primary-500" />
                                <span className={label}>Top Job Roles Tracked</span>
                            </div>
                            {data.demographics.top_roles.length === 0 ? (
                                <p className={subtext}>No tracking data yet.</p>
                            ) : (
                                <ol className="space-y-1.5">
                                    {data.demographics.top_roles.map((r, idx) => (
                                        <li key={idx} className="flex items-center justify-between gap-2">
                                            <span className={`text-sm truncate ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                                                <span className={`mr-2 text-xs font-mono ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>{idx + 1}.</span>
                                                {r.role}
                                            </span>
                                            <span className={`text-xs font-medium px-2 py-0.5 rounded-full flex-shrink-0
                                                ${isDarkMode ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-600'}`}>
                                                {r.count}
                                            </span>
                                        </li>
                                    ))}
                                </ol>
                            )}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
